/**
 * LDG Builder — Constructs the Learning Dependency Graph from taxonomy + prerequisites.
 *
 * ARCHITECTURE v4.0 — Phase 1
 *
 * The LDG is derived from:
 * 1. TAXONOMY array (263 family nodes)
 * 2. Prerequisite definitions (edges between families)
 *
 * Design principle N2: LDG is derived from taxonomy prerequisites — never manually maintained.
 */

import type {
  LDGraph,
  LDGNode,
  LDGEdge,
  LDGMetadata,
  PrerequisiteDefinition,
  FrontierFamily,
  FrontierResult,
  FamilyMasteryInput,
} from './types'
import { TAXONOMY, FAMILY_BY_ID, QUESTIONS_BY_FAMILY } from '../../content'
import { validateDAG } from './dagValidator'
import { warn as logWarn } from '../../utils/logger'

/**
 * Build the complete LDG from taxonomy and prerequisite definitions.
 *
 * This is called once at startup. The resulting graph is immutable
 * and shared across all consumers.
 */
export function buildLDG(
  prerequisiteDefinitions: PrerequisiteDefinition[],
): LDGraph {
  // 1. Validate the DAG first
  const validation = validateDAG(prerequisiteDefinitions)

  // 1b. Enforce validation — log errors and collect broken edges to filter
  const brokenEdges = new Set<string>()
  if (!validation.isValid) {
    for (const err of validation.errors) {
      if (
        err.type === 'TIER_MONOTONICITY_VIOLATION' ||
        err.type === 'CYCLE_DETECTED'
      ) {
        logWarn(`[LDG] ⚠️ ${err.type}: ${err.message}`)
        // Track broken edges: familyIds[1] = prerequisite, familyIds[0] = dependent
        if (err.familyIds.length >= 2) {
          brokenEdges.add(`${err.familyIds[1]}→${err.familyIds[0]}`)
        }
      }
    }
    if (brokenEdges.size > 0) {
      logWarn(`[LDG] Filtering ${brokenEdges.size} broken edge(s) from graph`)
    }
  }

  // 2. Build prerequisite lookup
  const prereqMap = new Map<string, string[]>()
  const depMap = new Map<string, string[]>()

  for (const familyDef of TAXONOMY) {
    prereqMap.set(familyDef.familyId, [])
    depMap.set(familyDef.familyId, [])
  }

  const edges: LDGEdge[] = []

  for (const def of prerequisiteDefinitions) {
    if (!FAMILY_BY_ID.has(def.familyId)) continue

    for (const prereq of def.prerequisites) {
      if (!FAMILY_BY_ID.has(prereq)) continue
      if (prereq === def.familyId) continue

      // Skip broken edges identified by validation
      if (brokenEdges.has(`${prereq}→${def.familyId}`)) continue

      // Add edge
      edges.push({
        from: prereq,
        to: def.familyId,
        type: def.type ?? 'hard',
        reason: def.notes,
      })

      // Update adjacency lists
      const prereqs = prereqMap.get(def.familyId) ?? []
      if (!prereqs.includes(prereq)) {
        prereqs.push(prereq)
        prereqMap.set(def.familyId, prereqs)
      }

      const deps = depMap.get(prereq) ?? []
      if (!deps.includes(def.familyId)) {
        deps.push(def.familyId)
        depMap.set(prereq, deps)
      }
    }
  }

  // 3. Compute depths
  const depths = new Map<string, number>()
  const depthComputed = new Set<string>()

  function computeDepth(familyId: string): number {
    if (depthComputed.has(familyId)) return depths.get(familyId) ?? 0

    const prereqs = prereqMap.get(familyId) ?? []
    if (prereqs.length === 0) {
      depths.set(familyId, 0)
      depthComputed.add(familyId)
      return 0
    }

    // Prevent infinite recursion on cycles
    depths.set(familyId, 0)
    depthComputed.add(familyId)

    const maxPrereqDepth = Math.max(...prereqs.map(computeDepth))
    const depth = maxPrereqDepth + 1
    depths.set(familyId, depth)
    return depth
  }

  for (const familyDef of TAXONOMY) {
    computeDepth(familyDef.familyId)
  }

  // 4. Build nodes
  const nodes = new Map<string, LDGNode>()

  for (const familyDef of TAXONOMY) {
    nodes.set(familyDef.familyId, {
      familyId: familyDef.familyId,
      module: familyDef.module,
      skill: familyDef.skill,
      subSkill: familyDef.subSkill,
      tier: familyDef.tier,
      levelCode: familyDef.levelCode,
      prerequisites: prereqMap.get(familyDef.familyId) ?? [],
      dependents: depMap.get(familyDef.familyId) ?? [],
      depth: depths.get(familyDef.familyId) ?? 0,
      hasContent:
        (QUESTIONS_BY_FAMILY.get(familyDef.familyId) || []).length > 0,
    })
  }

  // 4.5 Log phantom families (families without content)
  const phantomFamilies = Array.from(nodes.values()).filter(
    (n) => !n.hasContent,
  )
  if (phantomFamilies.length > 0) {
    logWarn(`📊 LDG: ${phantomFamilies.length} phantom families (no content):`)
    // Only log the first 10 to avoid console spam
    phantomFamilies
      .slice(0, 10)
      .forEach((n) =>
        logWarn(`   - ${n.familyId} (tier ${n.tier}, ${n.module})`),
      )
    if (phantomFamilies.length > 10) {
      logWarn(`   ... and ${phantomFamilies.length - 10} more`)
    }
  }

  // 5. Find roots and leaves
  const roots = Array.from(nodes.values())
    .filter((n) => n.prerequisites.length === 0)
    .map((n) => n.familyId)

  const leaves = Array.from(nodes.values())
    .filter((n) => n.dependents.length === 0)
    .map((n) => n.familyId)

  // 6. Topological order (from validation)
  const topologicalOrder = validation.isValid
    ? computeTopologicalOrder(
        prereqMap,
        TAXONOMY.map((f) => f.familyId),
      )
    : []

  // 7. Metadata
  const maxDepth = Math.max(...Array.from(depths.values()), 0)
  const metadata: LDGMetadata = {
    nodeCount: nodes.size,
    edgeCount: edges.length,
    maxDepth,
    componentCount: validation.stats.componentCount,
    isValidDAG: validation.isValid,
    validationErrors: validation.errors.map((e) => e.message),
    builtAt: new Date().toISOString(),
  }

  return {
    nodes,
    edges,
    prerequisites: prereqMap,
    dependents: depMap,
    roots,
    leaves,
    topologicalOrder,
    metadata,
  }
}

// ─── Topological Sort ─────────────────────────────────────────────────────────

function computeTopologicalOrder(
  prereqMap: Map<string, string[]>,
  allFamilyIds: string[],
): string[] {
  const inDegree = new Map<string, number>()
  for (const id of allFamilyIds) {
    inDegree.set(id, (prereqMap.get(id) ?? []).length)
  }

  const queue: string[] = []
  for (const [id, degree] of inDegree) {
    if (degree === 0) queue.push(id)
  }

  const order: string[] = []

  while (queue.length > 0) {
    // Sort queue by tier for deterministic output
    queue.sort((a, b) => {
      const tierA = FAMILY_BY_ID.get(a)?.tier ?? 0
      const tierB = FAMILY_BY_ID.get(b)?.tier ?? 0
      return tierA - tierB || a.localeCompare(b)
    })

    const node = queue.shift()!
    order.push(node)

    // Reduce in-degree of dependents
    for (const [dependent, prereqs] of prereqMap) {
      if (prereqs.includes(node)) {
        const newDegree = (inDegree.get(dependent) ?? 0) - 1
        inDegree.set(dependent, newDegree)
        if (newDegree === 0) {
          queue.push(dependent)
        }
      }
    }
  }

  return order
}

// ─── Frontier Calculation ─────────────────────────────────────────────────────

/**
 * Calculate the learning frontier for a learner based on their mastery state.
 *
 * The frontier consists of families where:
 * 1. All hard prerequisites are mastered
 * 2. The family itself is not yet mastered
 * 3. The family has content available
 *
 * Families are prioritized by:
 * - Lower tier first (easier content)
 * - Higher prerequisite mastery percentage
 * - Module diversity (spread across modules)
 */
export function calculateFrontier(
  graph: LDGraph,
  masteryInputs: FamilyMasteryInput[],
  options?: {
    /** Mastery threshold (0-100) to consider a family "mastered" */
    masteryThreshold?: number
    /** Maximum frontier size */
    maxFrontierSize?: number
    /** Filter to specific modules */
    moduleFilter?: string[]
  },
): FrontierResult {
  const masteryThreshold = options?.masteryThreshold ?? 80
  const maxFrontierSize = options?.maxFrontierSize ?? 50

  // Build mastery lookup
  const masteryMap = new Map<string, FamilyMasteryInput>()
  for (const input of masteryInputs) {
    masteryMap.set(input.familyId, input)
  }

  const mastered: string[] = []
  const locked: string[] = []
  const inProgress: string[] = []
  const frontier: FrontierFamily[] = []

  for (const [familyId, node] of graph.nodes) {
    // Skip if module filter is active and doesn't match
    if (options?.moduleFilter && !options.moduleFilter.includes(node.module)) {
      continue
    }

    const mastery = masteryMap.get(familyId)
    const isMastered = mastery
      ? mastery.masteryScore >= masteryThreshold
      : false
    const isInProgress = mastery ? mastery.attempts > 0 && !isMastered : false

    if (isMastered) {
      mastered.push(familyId)
      continue
    }

    if (isInProgress) {
      inProgress.push(familyId)
    }

    // Check prerequisites
    const hardPrereqs = node.prerequisites.filter((prereqId) => {
      const edge = graph.edges.find(
        (e) => e.from === prereqId && e.to === familyId,
      )
      return !edge || edge.type === 'hard'
    })

    const masteredPrereqs = hardPrereqs.filter((prereqId) => {
      const prereqMastery = masteryMap.get(prereqId)
      return prereqMastery
        ? prereqMastery.masteryScore >= masteryThreshold
        : false
    })

    const allPrereqsMet =
      hardPrereqs.length === 0 || masteredPrereqs.length === hardPrereqs.length

    if (!allPrereqsMet) {
      locked.push(familyId)
      continue
    }

    // This family is on the frontier
    const prereqPercentage =
      hardPrereqs.length > 0
        ? Math.round((masteredPrereqs.length / hardPrereqs.length) * 100)
        : 100

    // Priority: lower tier + higher prereq mastery + in-progress bonus
    const tierPriority = (17 - node.tier) * 10 // Higher for lower tiers
    const prereqPriority = prereqPercentage
    const progressBonus = isInProgress ? 20 : 0
    const priority = tierPriority + prereqPriority + progressBonus

    frontier.push({
      familyId,
      module: node.module,
      skill: node.skill,
      tier: node.tier,
      reason:
        hardPrereqs.length === 0
          ? 'root_node'
          : isInProgress
            ? 'in_progress'
            : 'prerequisites_met',
      prerequisitesMet: true,
      prerequisiteMastery: {
        mastered: masteredPrereqs.length,
        total: hardPrereqs.length,
        percentage: prereqPercentage,
      },
      priority,
    })
  }

  // Sort frontier by priority (descending)
  frontier.sort((a, b) => b.priority - a.priority)

  // Limit frontier size
  const limitedFrontier = frontier.slice(0, maxFrontierSize)

  // Group by module
  const byModule: Record<string, FrontierFamily[]> = {}
  for (const family of limitedFrontier) {
    if (!byModule[family.module]) {
      byModule[family.module] = []
    }
    byModule[family.module].push(family)
  }

  return {
    frontier: limitedFrontier,
    mastered,
    locked,
    inProgress,
    byModule,
  }
}

// ─── Graph Queries ────────────────────────────────────────────────────────────

/**
 * Get all ancestors (transitive prerequisites) of a family.
 */
export function getAncestors(graph: LDGraph, familyId: string): string[] {
  const ancestors = new Set<string>()
  const queue = [...(graph.prerequisites.get(familyId) ?? [])]

  while (queue.length > 0) {
    const current = queue.shift()!
    if (ancestors.has(current)) continue
    ancestors.add(current)
    queue.push(...(graph.prerequisites.get(current) ?? []))
  }

  return Array.from(ancestors)
}

/**
 * Get all descendants (transitive dependents) of a family.
 */
export function getDescendants(graph: LDGraph, familyId: string): string[] {
  const descendants = new Set<string>()
  const queue = [...(graph.dependents.get(familyId) ?? [])]

  while (queue.length > 0) {
    const current = queue.shift()!
    if (descendants.has(current)) continue
    descendants.add(current)
    queue.push(...(graph.dependents.get(current) ?? []))
  }

  return Array.from(descendants)
}

/**
 * Check if familyA is a prerequisite (direct or transitive) of familyB.
 */
export function isPrerequisiteOf(
  graph: LDGraph,
  familyA: string,
  familyB: string,
): boolean {
  return getAncestors(graph, familyB).includes(familyA)
}
